/**
 * Deutsche Meldungen fuer gescheiterte API-Aufrufe.
 *
 * Bewusst neben `formatApiError` aus `./validation` und nicht darin: das faellt
 * am Ende auf `err.message` zurueck, also auf das englische „Network Error" von
 * axios. An allen zwoelf Aufrufstellen steht dort `formatApiError(err) ||
 * 'deutscher Text'` — und der deutsche Text kommt nie zum Zug, weil die
 * englische Meldung nicht leer ist (Befund vom 29.10.2026). Wer hier aufruft,
 * bekommt in jedem Zweig einen deutschen Satz; einen Rueckfall auf die
 * Bibliothek gibt es nicht.
 *
 * `vorgang` benennt die Handlung im Nominativ mit Artikel („Der Statuswechsel",
 * „Das Umwandeln"). Nur zwei Zweige verwenden sie; die uebrigen Saetze sagen
 * etwas ueber die Verbindung oder den Datensatz und haengen nicht davon ab.
 */

type AxiosArtig = {
  response?: { status?: number; data?: { detail?: unknown } };
};

export function apiFehlerText(fehler: unknown, vorgang: string): string {
  const f = fehler as AxiosArtig;
  const code = f?.response?.status;

  // Kein `response`: so kommt ein Netzfehler bei axios an.
  if (code === undefined) {
    return 'Der Server ist nicht erreichbar. Verbindung prüfen und noch einmal versuchen.';
  }
  // Ein 401 kommt beim Aufrufer nur an, wenn `bei401` in `lib/api` die Sitzung
  // nicht erneuern konnte (core nicht erreichbar) oder die Wiederholung erneut
  // 401 bekam — sonst wiederholt der Client die Anfrage selbst.
  if (code === 401) {
    return 'Die Anmeldung ist nicht mehr gültig oder ließ sich nicht erneuern. Noch einmal versuchen; bleibt es dabei, neu anmelden.';
  }
  if (code === 403) {
    return `${vorgang} ist nicht erlaubt — dafür fehlt die Berechtigung.`;
  }
  if (code === 404) {
    return 'Der Datensatz ist nicht mehr vorhanden. Liste neu laden.';
  }
  if (code === 422) {
    return apiMeldung(f.response?.data?.detail) ?? 'Dazu fehlt eine Angabe.';
  }
  return `${vorgang} ist fehlgeschlagen (Fehler ${code}).`;
}

/**
 * Die Meldung unserer eigenen Validatoren; sie ist schon deutsch und nennt
 * seit dem 29.10.2026 keinen Spaltennamen mehr (`schemas/customer.py`). Steht
 * nichts Brauchbares darin, gibt diese Funktion `null` zurueck und der
 * Aufrufer nimmt seinen eigenen Satz — nie die Meldung der Bibliothek.
 */
export function apiMeldung(detail: unknown): string | null {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const texte = detail
      .map((d) => (typeof d?.msg === 'string' ? d.msg.replace(/^Value error, /, '') : ''))
      .filter(Boolean);
    if (texte.length) return texte.join('\n');
  }
  return null;
}
